import { useRouter } from 'next/navigation';

import React, { useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import { REWARD_POOL_DISTRIBUTION_POLICY, RewardPool } from '@shared/api/types/rewardPools';
import { AuthWeb3Modal } from '@shared/common/components/modals/AuthWeb3Modal';
import { useAuth } from '@shared/common/providers/AuthProvider';
import MarkCheckIcon from '@shared/constants/icons/MarkCheck';
import { getErrorMessage } from '@shared/helpers/error';
import useRerenderAfterDate from '@shared/hooks/useRerenderAfterDate';

import {
  isRewardPoolActive,
  REWARD_POOL_REQUIREMENTS_ANCHOR_ID,
  useIsJoinedToPool,
  useJoinRewardPool,
} from '@store:web/components/entities/rewardPool';

interface Props {
  rewardPool: RewardPool;
  onSuccess?: () => void;
}

const getButtonTitle = (rewardPool: RewardPool): string => {
  switch (rewardPool.distribution_policy) {
    case REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE:
    case REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2:
      return 'Join Raffle';
    default:
      return 'Join Pool';
  }
};

export const JoinRewardPoolButton = ({ rewardPool, onSuccess }: Props) => {
  const router = useRouter();
  const { mutateAsync: joinRewardPool, isPending: isJoinRewardPoolLoading } = useJoinRewardPool(
    rewardPool.id,
  );

  const { isAuth } = useAuth();
  const [isOpenEVMModal, setIsOpenEVMModal] = useState(false);

  const { isJoined: isSelfJoined, isLoading: isSelfJOinedRewardPoolLoading } = useIsJoinedToPool({
    rewardPool,
  });

  // NOTE: rerender component because some parts of ui depends on those dates
  useRerenderAfterDate(rewardPool.start_date);
  useRerenderAfterDate(rewardPool.end_date);

  const handleJoinPoolButtonClick = async () => {
    if (!isAuth) {
      setIsOpenEVMModal(true);
      return;
    }

    const requirementsHref = `/reward-pools/${rewardPool.id}#${REWARD_POOL_REQUIREMENTS_ANCHOR_ID}`;

    if (rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2) {
      router.push(requirementsHref);
      return;
    }

    try {
      await joinRewardPool({ rewardPool });

      onSuccess?.();
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      toast.error(errorMessage);

      router.push(requirementsHref);
    }
  };

  const availableSlots = (rewardPool.participants_max || 0) - rewardPool.participants_counter;

  const hasAvailableSlotsForFcfs =
    (rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.FCFS ||
      rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.FCFS_V2) &&
    availableSlots > 0;
  const hasAvailableSlotsForRaffle =
    (rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE ||
      rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2) &&
    (!rewardPool.participants_max || availableSlots > 0);

  const hasAvailableSlots =
    rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.PRO_RATA_KARMA ||
    hasAvailableSlotsForFcfs ||
    hasAvailableSlotsForRaffle;

  const isJoinLoading = isJoinRewardPoolLoading || isSelfJOinedRewardPoolLoading;

  const isActive = isRewardPoolActive(rewardPool);

  const isJoinButtonDisabled = isJoinLoading || isSelfJoined || !isActive || !hasAvailableSlots;

  return (
    <>
      <button
        disabled={isJoinButtonDisabled}
        onClick={handleJoinPoolButtonClick}
        className={classNames('msq-btn msq-btn-icon-lg msq-btn-primary w-full', {
          'bg-[#FCEDF5] text-[#09090B]': isSelfJoined,
          'msq-btn-loading': isJoinLoading,
        })}
        data-qa="join_reward_pool_button"
      >
        {isSelfJoined ? (
          <>
            <MarkCheckIcon className="text-[#D61F7B] msq-btn-icon-child msq-btn-icon-child-left" />
            Joined
          </>
        ) : (
          getButtonTitle(rewardPool)
        )}
      </button>
      <AuthWeb3Modal isOpen={isOpenEVMModal} setIsOpen={setIsOpenEVMModal} />
    </>
  );
};
